import React, { useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { getAccountByClientId } from "../../services/transactionService";
import { Box, Divider, IconButton, Typography, Link } from "@mui/material";
import { DataGrid, GridColDef, GridValueGetterParams } from "@mui/x-data-grid";
import { AccountType } from "../../types/genericType";
import { CloseOutlined } from "@mui/icons-material";
import TableRowsLoader from "../common/Loader/tableLoader";

type ClientDetailsModalProps = {
  selectedClientDetails: {
    id: string;
    name: string;
  };
  handleModalClose: Function;
};

const getRows = (clientDetails: Array<AccountType>) => {
  const rows: {
    id: number;
    accountNumber: string;
    name: string;
    balance: number;
  }[] = [];
  clientDetails?.forEach((account: AccountType) => {
    if (account) {
      rows.push({
        id: account?.id,
        accountNumber: account?.accountNumber,
        name: account?.name,
        balance: account?.balance,
      });
    }
  });
  return rows;
};

const ClientDetailsModal = (props: ClientDetailsModalProps) => {
  const [clientDetails, setClientDetails] = useState<AccountType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const location = useLocation();
  const {
    selectedClientDetails: { id, name },
    handleModalClose,
  } = props;

  const columns: GridColDef[] = useMemo<GridColDef[]>(
    () => [
      {
        field: "accountNumber",
        headerName: "Account #",
        minWidth: 70,
        headerClassName: "header-datagrid",
      },
      {
        field: "name",
        headerName: "Account Name",
        minWidth: 520,
        renderCell: (params) => (
          <Link
            className="client-cell"
            component={RouterLink}
            sx={{
              textDecoration: 'none',
            }}
            onClick={() => handleModalClose()}
            to={`${location?.pathname}?account=${params.row.accountNumber}&client=${id}`}>
            {params.row.name}
          </Link>
        )
      },
      {
        field: "balance",
        minWidth: 100,
        headerName: "Balance",
        valueGetter: (params: GridValueGetterParams) =>
          `$ ${params.row.balance.toFixed(2) || ""}`,
        headerClassName: "header-datagrid",
      },
    ],
    []
  );

  useEffect(() => {
    setIsLoading(true);
    getAccountByClientId(id?.toString() ?? "").then((res) => {
      setIsLoading(false);
      setClientDetails(res as AccountType[]);
    });
  }, [id]);

  return (
    <Box
      sx={{
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: "800px",
        maxWidth: "800px",
        maxHeight: "60%",
        overflowY: "auto",
        backgroundColor: "#fff",
        boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.5)",
        borderRadius: "5px",
        padding: "15px",
        zIndex: 9999,
      }}
    >
      {isLoading ? (
        <TableRowsLoader rowsNum={10} />
      ) : (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignContent: "center",
              marginBottom: "10px",
            }}
          >
            <Typography
              fontSize={20}
              fontWeight={500}
              color="black"
              sx={{ padding: "20px 0px 0px 20px" }}
            >
              {name}
            </Typography>
            <IconButton onClick={() => handleModalClose()} size="large">
              <CloseOutlined />
            </IconButton>
          </Box>
          <Divider />
          <DataGrid
            rows={getRows(clientDetails)}
            getRowId={(row) => row.id}
            columns={columns}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 10 },
              },
            }}
            pageSizeOptions={[10, 20, 30, 40, 50]}
            rowSelection={false}
            className="datagrid"
            disableRowSelectionOnClick
          />
        </>
      )}
    </Box>
  );
};

export default ClientDetailsModal;
